import { readClassPassOffer, type ClassPassOffer } from '@/features/clubPackages/classPassOffer'
import type { SessionBalanceClub, SessionBalancesResponse } from './sessionBalance'
import { chargeOf, taxFieldsOf, type TaxReadFields } from '@/features/payments/taxDisplay'

// A WEEK / MONTH / YEAR PASS ON `/me` (D4.14 W3, backend `me/services/helpers/sessionBalancePassRow.mjs`):
// it arrives among a club's `packages` as `kind: "pass"`, holding no count. It is lifted out into
// its own `passes` list here, at the one fetch, so no counted-row reader — the totals, the home
// block, the balance rows — ever meets a row with no `sessionsAllowed` (D11: never add the rows up).

/** The class pass fields (`programs/passOut.mjs`, the same as the class's `passOffer`), plus where it lives. */
export type SessionBalancePass = ClassPassOffer & TaxReadFields & {
  programId: string
  programTitle: string | null
  /** The holder's name, and only when it is not the viewer — `null` on your own pass. */
  holderName: string | null
  holderIsJunior: boolean
  /** What the card lane charges for it, in majors — its server total when taxed (D3.16); null = no price. */
  price: number | null
  currency: string | null
  /** A CHILD's pass names the child (`holderId` = their playerId, the enrol body's `dependentId`). */
  holderType: 'player' | 'dependent' | null
  holderId: string | null
}

function readPassRow(raw: unknown): SessionBalancePass | null {
  const pass = readClassPassOffer(raw)
  const row = (raw ?? {}) as Record<string, unknown>
  const programId = typeof row.programId === 'string' ? row.programId.trim() : ''
  if (!pass || !programId) return null
  const text = (value: unknown) => (typeof value === 'string' && value.trim() ? value.trim() : null)
  const price = row.price == null ? NaN : Number(row.price)
  const tax = taxFieldsOf(row as TaxReadFields)
  return {
    ...pass,
    ...tax,
    programId,
    programTitle: text(row.programTitle),
    holderName: text(row.holderName),
    holderIsJunior: row.holderIsJunior === true,
    price: Number.isFinite(price) && price >= 0 ? chargeOf(tax, price) : null,
    currency: text(row.currency),
    holderType: row.holderType === 'dependent' || row.holderType === 'player' ? row.holderType : null,
    holderId: text(row.holderId),
  }
}

const isPassRow = (row: unknown) => (row as { kind?: unknown } | null)?.kind === 'pass'

/** Each club's `packages` without its pass rows, and those rows as `passes`. */
export function splitPassRows(response: SessionBalancesResponse): SessionBalancesResponse {
  const clubs = Array.isArray(response?.clubs) ? response.clubs : []
  return {
    clubs: clubs.map((club): SessionBalanceClub => {
      const rows: unknown[] = Array.isArray(club.packages) ? club.packages : []
      return {
        ...club,
        packages: rows.filter((row) => !isPassRow(row)) as SessionBalanceClub['packages'],
        passes: rows.filter(isPassRow).flatMap((row) => readPassRow(row) ?? []),
      }
    }),
  }
}
